// pages/tabs/OtherSettingsTab.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Card, Button, Spinner, Badge, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchHouseToolsSummaryThunk,
  resetReceiptsThunk,
  resetBalanceThunk,
  clearHouseToolsMsg,
} from "../../../../store/slices/houseToolsSlice";

import { toast } from "react-toastify";
import "../../../../css/master-common.css";

const inrFmt = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const Money = ({ v }) => {
  const n = Number(v || 0);
  return <span>{inrFmt.format(Number.isFinite(n) ? n : 0)}</span>;
};

export default function OtherSettingsTab({  home_id,
  activeTab,
  onRefreshProfile, }) {
  const dispatch = useDispatch();

  const { summary, loading, resettingReceipts, resettingBalance, error, success } =
    useSelector((s) => s.houseTools);

  const t = useMemo(() => summary?.totals || {}, [summary]);
  const b = useMemo(() => summary?.balances || {}, [summary]);

  const disabledAll = !home_id || loading || resettingReceipts || resettingBalance;

  // ✅ IMPORTANT: must match HouseProfilePage tab key
  const isActive = activeTab === "other_settings";

  // ✅ modal state
  const [showConfirmReceipts, setShowConfirmReceipts] = useState(false);
  const [showConfirmBalance, setShowConfirmBalance] = useState(false);

  // Prevent duplicate toast on same message
  const lastMsgRef = useRef({ error: null, success: null });

  // ✅ fetch ONLY when tab opens (or home_id changes)
  useEffect(() => {
    if (!isActive) return;
    if (!home_id) return;
    dispatch(fetchHouseToolsSummaryThunk(home_id));
  }, [isActive, home_id, dispatch]);

  // ✅ Toast for redux error/success
  useEffect(() => {
    if (error && lastMsgRef.current.error !== error) {
      toast.error(error);
      lastMsgRef.current.error = error;
      dispatch(clearHouseToolsMsg());
    }
    if (success && lastMsgRef.current.success !== success) {
      toast.success(success);
      lastMsgRef.current.success = success;
      dispatch(clearHouseToolsMsg());
    }
  }, [error, success, dispatch]);

  const refresh = () => {
    if (!home_id) return;
    dispatch(fetchHouseToolsSummaryThunk(home_id));
  };

const doResetReceipts = async () => {
  if (!home_id) return;

  const r = await dispatch(resetReceiptsThunk(home_id));

  if (resetReceiptsThunk.fulfilled.match(r)) {
    toast.success("Receipts reset successfully");

    refresh();
    onRefreshProfile?.();
  } else {
    toast.error(
      r?.payload?.message ||
        r?.error?.message ||
        "Failed to reset receipts"
    );
  }
};

const doResetBalance = async () => {
  if (!home_id) return;

  const r = await dispatch(resetBalanceThunk(home_id));

  if (resetBalanceThunk.fulfilled.match(r)) {
    toast.success("Balance reset successfully");

    refresh();
    onRefreshProfile?.();
  } else {
    toast.error(
      r?.payload?.message ||
        r?.error?.message ||
        "Failed to reset balance"
    );
  }
};

  // ✅ optional: don't render heavy UI when hidden
  if (!isActive) return null;

  return (
    <div className="hm-form-page">
      {/* ... SAME UI AS YOUR CODE (no change below) ... */}
      {/* keep everything exactly same */}
      <Row className="g-3">
        <Col lg={6}>
          <Card className="hm-form-card">
            <Card.Header className="hm-form-header">
              <div className="hm-form-title-wrap" style={{ alignItems: "center" }}>
                <div>
                  <h5 className="mb-1 hm-form-title">Reset Receipts</h5>
                  <div className="hm-form-subtitle">
                    Recalculate receipt totals and bill totals for this house.
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  {home_id ? (
                    <Badge bg="light" text="dark" className="hm-form-badge">
                      Home ID: <span className="hm-form-badge-val">{home_id}</span>
                    </Badge>
                  ) : null}

                  <Button
                    size="sm"
                    variant="light"
                    className="master-btn"
                    onClick={refresh}
                    disabled={!home_id || loading}
                  >
                    {loading ? "..." : "Refresh"}
                  </Button>
                </div>
              </div>
            </Card.Header>

            <Card.Body className="hm-form-body">
              {loading && !summary ? (
                <div className="hm-form-loading">
                  <Spinner animation="border" />
                  <div className="mt-2 text-muted">Loading summary...</div>
                </div>
              ) : (
                <div className="small">
                  <div className="mb-2">
                    <Badge bg="secondary">Total Receipts</Badge>{" "}
                    <span className="fw-bold">{t.total_receipts ?? 0}</span>
                  </div>

                  <div className="mb-2">
                    <Badge bg="secondary">Total Bills</Badge>{" "}
                    <span className="fw-bold">{t.total_bills ?? 0}</span>
                  </div>

                  <hr />

                  <div className="mb-2">
                    Total Water Bill Amount: <b><Money v={t.total_water_bill_amount} /></b>
                  </div>

                  <div className="mb-2">
                    Total Water Bill Opening Balance: <b><Money v={b.op_water_balance} /></b>
                  </div>

                  <div className="mb-2">
                    Total Net Water Bill Amount: <b><Money v={t.total_net_water_bill_amount} /></b>
                  </div>

                  <div className="mb-2">
                    Total SLRM Bill Amount: <b><Money v={t.total_slrm_bill_amount} /></b>
                  </div>

                  <div className="mb-2">
                    Total Water RCT Amount: <b><Money v={t.total_water_rct_amount} /></b>
                  </div>

                  <div className="mb-2">
                    Total SLRM RCT Amount: <b><Money v={t.total_slrm_rct_amount} /></b>
                  </div>

                  <div className="mb-2">
                    Total RCT Amount: <b><Money v={t.total_rct_amount} /></b>
                  </div>

                  <div className="hm-actions">
                    <div className="hm-actions-left">
                      <span className="hm-help">
                        This will rebuild totals based on current bills & receipts.
                      </span>
                    </div>

                    <div className="hm-actions-right">
                      <Button
                        variant="primary"
                        className="hm-primary-btn"
                        onClick={() => setShowConfirmReceipts(true)}
                        disabled={disabledAll}
                      >
                        {resettingReceipts ? (
                          <>
                            <Spinner size="sm" animation="border" className="me-2" />
                            Resetting…
                          </>
                        ) : (
                          "Reset Receipts"
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="hm-form-card">
            <Card.Header className="hm-form-header">
              <div className="hm-form-title-wrap" style={{ alignItems: "center" }}>
                <div>
                  <h5 className="mb-1 hm-form-title">Reset Balance</h5>
                  <div className="hm-form-subtitle">
                    Recompute balances using bills, receipts and opening balances.
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <Button
                    size="sm"
                    variant="light"
                    className="master-btn"
                    onClick={refresh}
                    disabled={!home_id || loading}
                  >
                    {loading ? "..." : "Refresh"}
                  </Button>
                </div>
              </div>
            </Card.Header>

            <Card.Body className="hm-form-body">
              {loading && !summary ? (
                <div className="hm-form-loading">
                  <Spinner animation="border" />
                  <div className="mt-2 text-muted">Loading balances...</div>
                </div>
              ) : (
                <div className="small">
                  <div className="mb-2">
                    Current Water Balance: <b><Money v={b.current_water_balance} /></b>
                  </div>
                  <div className="mb-2">
                    New Water Balance: <b><Money v={b.new_water_balance} /></b>
                  </div>

                  <hr />

                  <div className="mb-2">
                    Current SLRM Balance: <b><Money v={b.current_slrm_balance} /></b>
                  </div>
                  <div className="mb-2">
                    New SLRM Balance: <b><Money v={b.new_slrm_balance} /></b>
                  </div>

                  <div className="hm-actions">
                    <div className="hm-actions-left">
                      <span className="hm-help">
                        Use this if you changed bills/receipts and balance looks wrong.
                      </span>
                    </div>

                    <div className="hm-actions-right">
                      <Button
                        variant="primary"
                        className="hm-primary-btn"
                        onClick={() => setShowConfirmBalance(true)}
                        disabled={disabledAll}
                      >
                        {resettingBalance ? (
                          <>
                            <Spinner size="sm" animation="border" className="me-2" />
                            Resetting…
                          </>
                        ) : (
                          "Reset Balance"
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ✅ modals unchanged */}
      <Modal
        show={showConfirmReceipts}
        onHide={() => setShowConfirmReceipts(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Reset Receipts</Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          This will recalculate receipt totals for this house.
          <br />
          Are you sure you want to continue?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className="master-btn"
            onClick={() => setShowConfirmReceipts(false)}
            disabled={resettingReceipts}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className="master-btn"
            onClick={async () => {
              setShowConfirmReceipts(false);
              await doResetReceipts();
            }}
            disabled={resettingReceipts || !home_id}
          >
            {resettingReceipts ? "Resetting..." : "Yes, Reset"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showConfirmBalance}
        onHide={() => setShowConfirmBalance(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Reset Balance</Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          This will recompute balances based on bills and receipts.
          <br />
          Are you sure you want to continue?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className="master-btn"
            onClick={() => setShowConfirmBalance(false)}
            disabled={resettingBalance}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className="master-btn"
            onClick={async () => {
              setShowConfirmBalance(false);
              await doResetBalance();
            }}
            disabled={resettingBalance || !home_id}
          >
            {resettingBalance ? "Resetting..." : "Yes, Reset"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}